import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import useDebouncedFunc from 'hooks/useDebouncedFunc';
import { getFilterDisplayString } from 'utils/getFilterDisplayString';
import { FilterFragment as Filter, FilterInput } from 'generated/graphql';
import { DEBOUNCE_TIME, ReportsDashboardFiltersContextProps } from './reports.utils';
import { useReportsDashboard } from './useReportsDashboard';

const Context = createContext<ReportsDashboardFiltersContextProps | undefined>(undefined);

export const ReportsDashboardFiltersProvider = ({ children }: { children: ReactNode }) => {
  const { reportsDashboardState, setReportsDashboardState } = useReportsDashboard();
  const { filters, searchValue } = reportsDashboardState;

  const [productFilters, setProductFilters] = useState<Filter[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const handleFilter = useCallback(
    (value: string = '', filterInput = filters) => {
      if (filterInput) {
        setReportsDashboardState((prev) => ({ ...prev, filters: filterInput }));
        value = getFilterDisplayString(filterInput, productFilters, value);
      }

      if (value !== searchValue) {
        setReportsDashboardState((prev) => ({ ...prev, filteredSearchValue: value }));
      }
    },
    [productFilters, searchValue, filters, setReportsDashboardState],
  );

  const handleSearch = useDebouncedFunc((value: string) => {
    handleFilter(value);
  }, DEBOUNCE_TIME);

  const handleFiltersApplied = useDebouncedFunc((filterInput: FilterInput[], searchValueArg: string = '') => {
    const normalized = filterInput.map((input) => ({
      type: input.type,
      selectedIDs: input.selectedIDs,
    }));
    handleFilter(searchValueArg || searchValue, normalized);
  }, DEBOUNCE_TIME);

  const value = useMemo(
    () => ({
      filterOpen,
      setFilterOpen,
      productFilters,
      setProductFilters,
      handleFilter,
      handleFiltersApplied,
      handleSearch,
      onSearchChange: (val: string) => handleSearch(val),
    }),
    [filterOpen, productFilters, setProductFilters, handleFilter, handleFiltersApplied, handleSearch],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useReportsDashboardFilters = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useReportsDashboardFilters must be used within a ReportsDashboardFiltersProvider');
  }
  return result;
};
